import { isNil } from '@lucidlink/tscore';
import { isLevelEnabled, LogLevel } from '#common/logging';
import { NotImplementedError } from '#common/types';
import { stringifyReplacerForBigInt } from '#common/util';
export const getPrintIndentation = (indentLevel) => !isNil(indentLevel) ? '\t'.repeat(indentLevel) : '';
function isArrayOrMap(obj) {
    return !isNil(obj) && (obj instanceof Map || Array.isArray(obj));
}
function resetStats(stats) {
    Object.entries(stats).forEach(([key, value]) => {
        resetHelperRecursive(stats, key, value);
    });
}
function setTrace(stats, trace) {
    Object.values(stats).forEach((value) => {
        setTraceRecursive(value, trace);
    });
}
function stopTrace(stats) {
    Object.values(stats).forEach((value) => {
        stopTraceRecursive(value);
    });
}
function resetHelperRecursive(obj, key, value) {
    if (typeof value === 'string') {
        return; // This means that the field is used for name or something else but not stats
    }
    if (value instanceof CallStats || value instanceof StatsCommon || value instanceof BasicStats) {
        value.reset();
        return;
    }
    if (typeof value === 'number') {
        const objKey = key;
        obj[objKey] = 0;
        return;
    }
    if (isArrayOrMap(value)) {
        for (const [innerKey, innerValue] of value.entries()) {
            resetHelperRecursive(value, innerKey, innerValue);
        }
        return;
    }
    // If you encounter this error this means that you inherited from either
    // StatsCommon or CallStats and a new member is introduced that has no reset
    // support. Override or extend this method in order to reset properly!
    throw new NotImplementedError(`Unsupported stat type: ${typeof value}`);
}
function setTraceRecursive(value, trace) {
    if (typeof value === 'string' || typeof value === 'number' || value instanceof BasicStats) {
        return;
    }
    if (value instanceof CallStats || value instanceof StatsCommon) {
        value.setTrace(trace);
        return;
    }
    if (isArrayOrMap(value)) {
        for (const innerValue of value.values()) {
            setTraceRecursive(innerValue, trace);
        }
        return;
    }
    // If you encounter this error this means that you inherited from either
    // StatsCommon or CallStats and a new member is introduced that has no reset
    // support. Override or extend this method in order to set trace properly!
    throw new NotImplementedError(`Unsupported stat type: ${typeof value}`);
}
function stopTraceRecursive(value) {
    if (typeof value === 'string' || typeof value === 'number' || value instanceof BasicStats) {
        return;
    }
    if (value instanceof CallStats || value instanceof StatsCommon) {
        value.stopTrace();
        return;
    }
    if (isArrayOrMap(value)) {
        for (const innerValue of value.values()) {
            stopTraceRecursive(innerValue);
        }
        return;
    }
    // If you encounter this error this means that you inherited from either
    // StatsCommon or CallStats and a new member is introduced that has no reset
    // support. Override or extend this method in order to stop trace properly!
    throw new NotImplementedError(`Unsupported stat type: ${typeof value}`);
}
function serializeHelperRecursive(value) {
    if (typeof value === 'string') {
        return undefined; // This means that the field is used for name or something else but not stats
    }
    if (typeof value === 'number') {
        return value;
    }
    if (value instanceof CallStats || value instanceof StatsCommon || value instanceof BasicStats) {
        return value.serialize();
    }
    if (Array.isArray(value)) {
        const result = [];
        for (const innerValue of value) {
            const serializedValue = serializeHelperRecursive(innerValue);
            result.push(serializedValue);
        }
        return result;
    }
    if (value instanceof Map) {
        const result = new Map();
        for (const [innerKey, innerValue] of value.entries()) {
            const serializedValue = serializeHelperRecursive(innerValue);
            result.set(innerKey, serializedValue);
        }
        return result;
    }
    // If you encounter this error this means that you inherited from either
    // StatsCommon or CallStats and a new member is introduced that has no serialize
    // support. Override this method in order to serialize properly!
    throw new NotImplementedError(`Unsupported stat type: ${typeof value} for serialization`);
}
export class BasicStats {
    numSamples = 0;
    minSample = 0;
    maxSample = 0;
    totalSamples = 0;
    lastSample = 0;
    count() {
        return this.numSamples;
    }
    min() {
        return this.minSample;
    }
    max() {
        return this.maxSample;
    }
    total() {
        return this.totalSamples;
    }
    avg() {
        return this.numSamples === 0 ? 0 : this.totalSamples / this.numSamples;
    }
    last() {
        return this.lastSample;
    }
    serialize() {
        return {
            avg: this.avg(),
            count: this.count(),
            max: this.max(),
            min: this.min(),
            total: this.total(),
            lastSample: this.last(),
        };
    }
    update(sample) {
        ++this.numSamples;
        if (this.numSamples == 1 || sample < this.minSample) {
            this.minSample = sample;
        }
        if (sample > this.maxSample) {
            this.maxSample = sample;
        }
        this.totalSamples += sample;
        this.lastSample = sample;
    }
    reset() {
        resetStats(this);
    }
}
export var OperationResult;
(function (OperationResult) {
    OperationResult["SUCCESS"] = "success";
    OperationResult["FAILURE"] = "failure";
})(OperationResult || (OperationResult = {}));
export class CallStats {
    name;
    log;
    success = new BasicStats();
    fail = new BasicStats();
    trace;
    constructor(name, log) {
        this.name = name;
        this.log = log;
    }
    setTraceInternal(trace) {
        this.trace = trace;
    }
    stopTraceInternal() {
        this.trace = undefined;
    }
    statsName() {
        return this.name;
    }
    count() {
        return this.success.count();
    }
    total() {
        return this.success.total();
    }
    min() {
        return this.success.min();
    }
    max() {
        return this.success.max();
    }
    avg() {
        return this.success.avg();
    }
    beginCall(context) {
        this.logBeginCall(context);
        return performance.now();
    }
    endCall(beginCallTime, result, context) {
        const durationMs = performance.now() - beginCallTime;
        if (result === OperationResult.SUCCESS) {
            this.success.update(durationMs);
        }
        else {
            this.fail.update(durationMs);
        }
        this.trace?.addAsyncEvent(this.name, beginCallTime, durationMs, 'Call Stats');
        this.logEndCall(result, context);
    }
    reset() {
        Object.entries(this).forEach(([key, value]) => {
            if (CallStats.isKeyNotResettable(key)) {
                return;
            }
            resetHelperRecursive(this, key, value);
        });
    }
    setTrace(trace) {
        this.setTraceInternal(trace);
        Object.entries(this).forEach(([key, value]) => {
            if (CallStats.isKeyNotResettable(key)) {
                return;
            }
            setTraceRecursive(value, trace);
        });
    }
    stopTrace() {
        this.stopTraceInternal();
        Object.entries(this).forEach(([key, value]) => {
            if (CallStats.isKeyNotResettable(key)) {
                return;
            }
            stopTraceRecursive(value);
        });
    }
    serialize() {
        const result = {
            name: this.name,
        };
        Object.entries(this).forEach(([key, value]) => {
            if (CallStats.isKeyNotResettable(key)) {
                return;
            }
            const resultKey = key;
            const resultValue = serializeHelperRecursive(value);
            if (!isNil(resultValue)) {
                result[resultKey] = resultValue;
            }
        });
        return result;
    }
    toString(_indentLevel) {
        const min = CallStats.toFixed(this.success.min());
        const max = CallStats.toFixed(this.success.max());
        const total = CallStats.toFixed(this.success.total());
        const avg = CallStats.toFixed(this.success.avg());
        const totalFailed = CallStats.toFixed(this.fail.total());
        return `${this.name} count=${this.count()} total=${total} ms min=${min} ms max=${max} ms avg=${avg} ms failed=${this.fail.count()} failedTotal=${totalFailed} ms`;
    }
    static toFixed(num) {
        return num.toFixed(3);
    }
    static isKeyNotResettable(key) {
        return key === 'name' || key === 'log' || key === 'trace';
    }
    logBeginCall(context) {
        if (isLevelEnabled(LogLevel.TRACE, this.log)) {
            this.log.trace(`Executing ${this.name}${this.contextToString(context)}...`);
        }
    }
    logEndCall(result, context) {
        if (isLevelEnabled(LogLevel.TRACE, this.log)) {
            this.log.trace(`${this.outcomeToString(result)}${this.contextToString(context)}.`);
        }
    }
    outcomeToString(result) {
        return `${this.name} ${result === OperationResult.SUCCESS ? 'executed successfully' : 'failed'}`;
    }
    contextToString(context) {
        return isNil(context) ? '' : ` (${JSON.stringify(context, stringifyReplacerForBigInt)})`;
    }
}
export class StatsCommon {
    toString(indentLevel) {
        const indentation = getPrintIndentation(indentLevel);
        let result = '';
        const keys = Object.keys(this);
        keys.forEach((key) => {
            const member = this[key];
            if (member instanceof CallStats) {
                // the line break before the first line is added for a better layout in logs
                result += '\n' + indentation;
                result += member.toString(indentLevel);
                return;
            }
            if (member instanceof StatsCommon) {
                // the line break before the first line is added for a better layout in logs
                result += `\n${indentation}${key}:`;
                result += member.toString(!isNil(indentLevel) ? indentLevel + 1 : undefined);
            }
        });
        return result;
    }
    reset() {
        resetStats(this);
    }
    setTrace(trace) {
        setTrace(this, trace);
    }
    stopTrace() {
        stopTrace(this);
    }
    serialize() {
        const result = {};
        Object.entries(this).forEach(([key, value]) => {
            const resultKey = key;
            const resultValue = serializeHelperRecursive(value);
            if (!isNil(resultValue)) {
                result[resultKey] = resultValue;
            }
        });
        return result;
    }
}
export async function withStats(stats, action, context) {
    const beginTime = stats.beginCall(context);
    try {
        const result = await action();
        stats.endCall(beginTime, OperationResult.SUCCESS, context);
        return result;
    }
    catch (e) {
        stats.endCall(beginTime, OperationResult.FAILURE);
        throw e;
    }
}
export function withStatsSync(stats, action, context) {
    const beginTime = stats.beginCall(context);
    try {
        const result = action();
        stats.endCall(beginTime, OperationResult.SUCCESS, context);
        return result;
    }
    catch (e) {
        stats.endCall(beginTime, OperationResult.FAILURE, context);
        throw e;
    }
}
